import React from 'react';
import { motion } from "motion/react";

const SkeletonPulse: React.FC<{ className?: string; delay?: number }> = ({ 
  className = "", 
  delay = 0 
}) => (
  <motion.div
    className={`bg-gray-700 rounded ${className}`}
    animate={{
      opacity: [0.4, 0.8, 0.4],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      delay,
      ease: "easeInOut"
    }}
  />
);

export const TicketCardSkeleton: React.FC = () => (
  <motion.div
    className="w-full bg-gray-800 border border-gray-600 rounded-lg overflow-hidden"
    style={{
      clipPath: `polygon(
        0 0,
        20px 0, 25px 4px, 30px 0, 35px 4px, 40px 0, 45px 4px, 50px 0, 55px 4px, 60px 0, 65px 4px, 70px 0, 75px 4px, 80px 0, 85px 4px, 90px 0, 95px 4px, 100px 0,
        calc(100% - 100px) 0, calc(100% - 95px) 4px, calc(100% - 90px) 0, calc(100% - 85px) 4px, calc(100% - 80px) 0, calc(100% - 75px) 4px, calc(100% - 70px) 0, calc(100% - 65px) 4px, calc(100% - 60px) 0, calc(100% - 55px) 4px, calc(100% - 50px) 0, calc(100% - 45px) 4px, calc(100% - 40px) 0, calc(100% - 35px) 4px, calc(100% - 30px) 0, calc(100% - 25px) 4px, calc(100% - 20px) 0, calc(100% - 15px) 4px, calc(100% - 10px) 0,
        100% 0,
        100% calc(50% - 20px),
        calc(100% - 15px) 50%,
        100% calc(50% + 20px),
        100% 100%,
        calc(100% - 10px) 100%, calc(100% - 15px) calc(100% - 4px), calc(100% - 20px) 100%, calc(100% - 25px) calc(100% - 4px), calc(100% - 30px) 100%, calc(100% - 35px) calc(100% - 4px), calc(100% - 40px) 100%, calc(100% - 45px) calc(100% - 4px), calc(100% - 50px) 100%, calc(100% - 55px) calc(100% - 4px), calc(100% - 60px) 100%, calc(100% - 65px) calc(100% - 4px), calc(100% - 70px) 100%, calc(100% - 75px) calc(100% - 4px), calc(100% - 80px) 100%, calc(100% - 85px) calc(100% - 4px), calc(100% - 90px) 100%, calc(100% - 95px) calc(100% - 4px), calc(100% - 100px) 100%,
        100px 100%, 95px calc(100% - 4px), 90px 100%, 85px calc(100% - 4px), 80px 100%, 75px calc(100% - 4px), 70px 100%, 65px calc(100% - 4px), 60px 100%, 55px calc(100% - 4px), 50px 100%, 45px calc(100% - 4px), 40px 100%, 35px calc(100% - 4px), 30px 100%, 25px calc(100% - 4px), 20px 100%, 15px calc(100% - 4px), 10px 100%,
        0 100%,
        0 calc(50% + 20px),
        15px 50%,
        0 calc(50% - 20px),
        0 0
      )`
    }}
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8
    }}
  >
    {/* Mobile skeleton */}
    <div className="sm:hidden p-3">
      <div className="flex justify-between items-start mb-2">
        <div className="space-y-1">
          <SkeletonPulse className="h-3 w-20" delay={0.1} />
          <SkeletonPulse className="h-2 w-16" delay={0.2} />
        </div>
      </div>
      <SkeletonPulse className="h-4 w-full mb-3" delay={0.3} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <SkeletonPulse className="h-3 w-3 rounded-full" delay={0.4} />
          <SkeletonPulse className="h-3 w-20" delay={0.5} />
        </div>
        <div className="flex items-center gap-2">
          <SkeletonPulse className="h-3 w-3 rounded-full" delay={0.6} />
          <SkeletonPulse className="h-3 w-16" delay={0.7} />
        </div>
      </div>
      <div className="pt-2 border-t border-gray-700 border-dashed">
        <SkeletonPulse className="h-2 w-24 mx-auto" delay={0.8} />
      </div>
    </div>

    {/* Desktop skeleton */}
    <div className="hidden sm:flex w-full relative">
      <div className="p-4 lg:p-6 flex-1 pl-6 lg:pl-8">
        <div className="flex justify-between items-start mb-3 lg:mb-4">
          <div className="space-y-1">
            <SkeletonPulse className="h-3 w-24" delay={0.1} />
            <SkeletonPulse className="h-2 w-16" delay={0.2} />
          </div>
          <SkeletonPulse className="h-2 w-16" delay={0.3} />
        </div>
        <SkeletonPulse className="h-5 lg:h-6 w-3/4 mb-3 lg:mb-4" delay={0.4} />
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <SkeletonPulse className="h-4 w-4 rounded-full" delay={0.5} />
            <SkeletonPulse className="h-3 w-8" delay={0.6} />
            <SkeletonPulse className="h-3 w-32" delay={0.7} />
          </div>
          <div className="flex items-center gap-2">
            <SkeletonPulse className="h-4 w-4 rounded-full" delay={0.8} />
            <SkeletonPulse className="h-3 w-8" delay={0.9} />
            <SkeletonPulse className="h-3 w-20" delay={1.0} />
          </div>
        </div>
        <div className="mt-3 lg:mt-4 pt-2 lg:pt-3 border-t border-gray-700 border-dashed">
          <SkeletonPulse className="h-2 w-48" delay={1.1} />
        </div>
      </div>

      {/* Perforated divider skeleton */}
      <div className="relative w-px bg-gray-700 mx-3 lg:mx-4">
        <div className="absolute inset-0 flex flex-col justify-between py-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonPulse 
              key={i} 
              className="w-1 h-1 rounded-full -translate-x-1/2" 
              delay={1.2 + i * 0.1} 
            />
          ))}
        </div>
      </div>

      {/* Stub section skeleton */}
      <div className="flex flex-col items-center justify-center p-4 lg:p-6 pr-6 lg:pr-8 min-w-[80px] lg:min-w-[120px]">
        <SkeletonPulse className="h-4 w-16 -rotate-90 mb-2" delay={1.3} />
        <SkeletonPulse className="h-3 w-12 -rotate-90" delay={1.4} />
      </div>
    </div>

    {/* Circular cutouts skeleton */}
    <div className="absolute top-1/2 -translate-y-1/2 -left-4 lg:-left-5 w-8 h-8 lg:w-10 lg:h-10 bg-gray-900 rounded-full"></div>
    <div className="absolute top-1/2 -translate-y-1/2 -right-4 lg:-right-5 w-8 h-8 lg:w-10 lg:h-10 bg-gray-900 rounded-full"></div>
  </motion.div>
);
